import { memoryStore } from '@debbie/common/dist/cache/store/index.js';
import { useEffect, useState } from 'react';
import * as loadable from './Loadable/utils.js';
const toLoadingState = (e) => {
    switch (e.kind) {
        case 'AwaitingReload': {
            return e.data ? loadable.createLoaded(e.data) : loadable.createNotLoaded();
        }
        case 'NotLoaded': {
            return loadable.createNotLoaded();
        }
        case 'Loading': {
            return loadable.createLoading();
        }
        case 'Error': {
            return loadable.createError(e.error);
        }
        case 'Reloading': {
            return loadable.createReloading(e.data);
        }
        case 'Success': {
            return loadable.createLoaded(e.data);
        }
    }
};
const onIdle = (fn) => {
    if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(fn);
    }
    else if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(fn);
    }
    else {
        setTimeout(fn, 0);
    }
};
const xc = (cache) => {
    const prefixKey = (key) => `K:${key}`;
    const prefixDep = (key) => `D:${key}`;
    let ids = 0;
    const collectUnused = () => {
        for (const key of cache.keys()) {
            const val = cache.get(key);
            if (val != null &&
                typeof val === 'object' &&
                'listeners' in val &&
                val.value.kind !== 'Loading' &&
                val.value.kind !== 'Reloading' &&
                Object.keys(val.listeners).length === 0 &&
                Date.now() > val.updatedAt + val.expireTime) {
                // Elligible for garbage collection
                cache.del(key);
            }
        }
        setTimeout(() => {
            onIdle(collectUnused);
        }, 1000 * 30);
    };
    setTimeout(() => {
        onIdle(collectUnused);
    }, 1000);
    const addListener = (key, fn) => {
        const entry = cache.get(prefixKey(key));
        if (entry != null) {
            const id = `${ids++}`;
            entry.listeners[id] = fn;
            return [key, id];
        }
    };
    const removeListener = (id) => {
        if (id != null) {
            // Would not work with a remote store
            delete cache.get(prefixKey(id[0]))?.listeners[id[1]];
        }
    };
    const set = (key, val, dependencies, expireTime = 1000 * 60 * 5) => {
        const entry = cache.get(prefixKey(key));
        if (entry == null) {
            cache.set(prefixKey(key), {
                value: val,
                reloadRequested: false,
                expireTime: expireTime,
                updatedAt: Date.now(),
                createdAt: Date.now(),
                listeners: {},
            });
        }
        const safeEntry = cache.get(prefixKey(key));
        if (safeEntry.reloadRequested &&
            val.kind !== 'Loading' &&
            val.kind !== 'Reloading') {
            val = {
                kind: 'AwaitingReload',
                data: val.kind === 'Success'
                    ? val.data
                    : safeEntry.value.kind === 'Reloading'
                        ? safeEntry.value.data
                        : undefined,
            };
            safeEntry.reloadRequested = false;
        }
        safeEntry.value = val;
        safeEntry.updatedAt = Date.now();
        safeEntry.expireTime = expireTime;
        for (const id in safeEntry.listeners) {
            safeEntry.listeners[id](val);
        }
        for (const dep of dependencies) {
            const depKey = prefixDep(dep);
            cache.setAdd(depKey, key);
        }
    };
    const get = (key) => {
        const entry = cache.get(prefixKey(key));
        if (entry == null) {
            return undefined;
        }
        return entry.value;
    };
    const query = (key, queryFn, dependencies, options = {}) => {
        if (get(key) == null) {
            set(key, { kind: 'NotLoaded' }, dependencies, options.expireTime);
        }
        const [value, setValue] = useState(get(key) ?? { kind: 'NotLoaded' });
        useEffect(() => {
            const id = addListener(key, (val) => {
                setValue(val);
            });
            const entry = get(key);
            if (value !== entry) {
                setValue(entry);
            }
            if ((options.enabled ?? true) &&
                (entry.kind === 'NotLoaded' || entry.kind === 'AwaitingReload')) {
                set(key, entry.kind === 'AwaitingReload' && entry.data
                    ? { kind: 'Reloading', data: entry.data }
                    : { kind: 'Loading' }, dependencies);
                Promise.resolve()
                    .then(queryFn)
                    .then((response) => {
                    set(key, { kind: 'Success', data: response }, dependencies, options.expireTime);
                    if (options.onSuccess) {
                        options.onSuccess(response);
                    }
                })
                    .catch((err) => {
                    set(key, { kind: 'Error', error: err }, dependencies, options.expireTime);
                    if (options.onError) {
                        options.onError(err);
                    }
                });
            }
            return () => {
                removeListener(id);
            };
        }, [key, value, options.enabled]);
        const val = get(key);
        if (val != value) {
            return val ? toLoadingState(val) : loadable.createNotLoaded();
        }
        return toLoadingState(value);
    };
    const ensureLoaded = async (key) => new Promise((resolve, reject) => {
        const entry = get(key);
        if (entry && (entry.kind === 'Success' || entry.kind === 'Error')) {
            resolve();
        }
        const id = addListener(key, (val) => {
            if (val.kind === 'Success' || val.kind === 'Error') {
                resolve();
                removeListener(id);
            }
        });
    });
    const prefetch = (prefetchKey, fn, splits, options = {}) => {
        if (options.enabled === false) {
            return;
        }
        prefetchKey = `PREFETCH-${prefetchKey}`;
        if (cache.get(prefetchKey) != null) {
            return;
        }
        cache.set(prefetchKey, true, {
            expireTime: options.expireTime ?? 1000 * 60 * 5,
        });
        for (const key in splits) {
            const entry = get(key);
            const split = splits[key];
            if (split.dynamicKey == null &&
                typeof split.dependencies != 'function' &&
                (entry == null || entry.kind === 'NotLoaded' || entry.kind === 'Error')) {
                set(key, { kind: 'Loading' }, split.dependencies, options.expireTime);
            }
        }
        return Promise.resolve(fn())
            .then((res) => {
            for (const key in splits) {
                const split = splits[key];
                set(split.dynamicKey ? split.dynamicKey(res) : key, { kind: 'Success', data: split.extract(res) }, typeof split.dependencies === 'function'
                    ? split.dependencies(res)
                    : split.dependencies, options.expireTime);
                if (options.onSuccess) {
                    options.onSuccess(res);
                }
            }
        })
            .catch((err) => {
            for (const key in splits) {
                const entry = get(key);
                const split = splits[key];
                if (typeof split.dependencies !== 'function' &&
                    entry != null &&
                    entry.kind === 'Loading') {
                    // We just default to act like the prefetch didn't happen and then locally components can refetch using the normal way
                    set(key, { kind: 'NotLoaded' }, split.dependencies, options.expireTime);
                }
            }
        });
    };
    const invalidate = (keys) => {
        for (const key of keys) {
            const val = cache.get(prefixKey(key));
            if (val) {
                if (val.value.kind === 'Loading' || val.value.kind === 'Reloading') {
                    val.reloadRequested = true;
                }
                else {
                    val.value =
                        val.value.kind === 'AwaitingReload' || val.value.kind === 'Success'
                            ? { kind: 'AwaitingReload', data: val.value.data }
                            : { kind: 'NotLoaded' };
                    for (const listener of Object.values(val.listeners)) {
                        listener(val.value);
                    }
                }
            }
            const deps = cache.get(prefixDep(key));
            if (deps != null) {
                invalidate([...deps.keys()]);
            }
        }
    };
    const mutateCache = (key, mfn) => {
        const entry = cache.get(prefixKey(key));
        if (entry == null) {
            return;
        }
        if (entry.value.kind !== 'Success') {
            return;
        }
        entry.value.data = mfn(entry.value.data);
        entry.updatedAt = Date.now();
        const val = { ...entry.value };
        for (const id in entry.listeners) {
            entry.listeners[id](val);
        }
    };
    return {
        query,
        invalidate,
        prefetch,
        mutateCache,
        ensureLoaded,
    };
};
const q = xc(memoryStore.create({ defaultExpireTime: Infinity }));
export default q;
//# sourceMappingURL=query.js.map